import {
  Add,
  ArrowDropDown,
  ArrowForward,
  CloudSync,
  Search,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  ClickAwayListener,
  FormControlLabel,
  Grow,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useMemo, useRef, useState } from 'react';
import { useDialogInboxList } from '../hooks/reactQuerys/DialogInboxQueries';
import {
  DialogInboxImportFormat,
  IDialogInboxRow,
} from '../Interfaces/IDialogInbox';
import { DialogInboxDetail } from './DialogInboxDetail';
import {
  DialogInboxImportDialog,
  FORMAT_OPTIONS,
} from './DialogInboxImportDialog';
import {
  OpenFormulareCard,
  useOpenFormulareStatusViewModel,
} from './OpenFormulareSource';
import { useOpenFormularePull } from '../hooks/reactQuerys/DialogInboxQueries';
import { useAlert } from '../context/AlertContext';
import { STATUS_COLOR, formatGermanDate, labelForStatus } from './i18n';

const matchesSearch = (row: IDialogInboxRow, needle: string): boolean => {
  if (!needle) return true;
  const haystack = [
    row.Title,
    row.DialogTitle,
    row.TransactionTypeHint,
    labelForStatus(row.Status),
    row.KiDiaId,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
};

export const DialogInboxOverview: React.FC = () => {
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [initialFormat, setInitialFormat] = useState<
    DialogInboxImportFormat | undefined
  >(undefined);
  const [isFormatMenuOpen, setIsFormatMenuOpen] = useState(false);
  const splitButtonRef = useRef<HTMLDivElement | null>(null);

  const { showAlert } = useAlert();
  const { dialogInboxList, isLoadingDialogInboxList, refetchDialogInboxList } =
    useDialogInboxList(showAll);
  const ofVm = useOpenFormulareStatusViewModel();
  const { pullOpenFormulare, isPullingOpenFormulare } = useOpenFormularePull();

  const triggerOpenFormularePull = async () => {
    if (!ofVm.isReady) return;
    setIsFormatMenuOpen(false);
    try {
      const result = await pullOpenFormulare();
      if (result.status === 'ok') {
        if (result.imported > 0) {
          showAlert({
            text: `${result.imported} neue${result.imported === 1 ? 'r Eintrag wurde' : ' Einträge wurden'} aus OpenFormulare übernommen.`,
            severity: 'success',
          });
        } else {
          showAlert({
            text: 'Keine neuen Einträge in OpenFormulare vorhanden.',
            severity: 'info',
          });
        }
        await refetchDialogInboxList();
      } else {
        showAlert({
          text:
            result.message ?? 'OpenFormulare konnte nicht abgerufen werden.',
          severity: 'warning',
        });
      }
    } catch (e) {
      console.error(e);
      showAlert({
        text: 'Abruf von OpenFormulare fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const filteredRows = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return (dialogInboxList ?? []).filter(row => matchesSearch(row, needle));
  }, [dialogInboxList, searchTerm]);

  const openImport = (format?: DialogInboxImportFormat) => {
    setInitialFormat(format);
    setIsImportOpen(true);
    setIsFormatMenuOpen(false);
  };

  const columns: GridColDef<IDialogInboxRow>[] = [
    {
      field: 'SubmittedAt',
      headerName: 'Eingegangen',
      flex: 1,
      renderCell: ({ row }) => formatGermanDate(row.SubmittedAt, true),
    },
    { field: 'DialogTitle', headerName: 'Dialog', flex: 1 },
    { field: 'Title', headerName: 'Titel', flex: 2 },
    {
      field: 'ParticipantCount',
      headerName: 'Beteiligte',
      width: 110,
      type: 'number',
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 160,
      renderCell: ({ row }) => {
        const effective =
          row.Status === 'processed' &&
          row.LegalTransaction_idLegalTransaction != null
            ? 'created'
            : row.Status;
        return (
          <Chip
            size='small'
            label={labelForStatus(effective)}
            color={STATUS_COLOR[effective] ?? 'default'}
          />
        );
      },
    },
  ];

  const isListEmpty =
    !isLoadingDialogInboxList && (dialogInboxList?.length ?? 0) === 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant='h5'>Importbereich</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            Eingang für Vorgangsdaten aus XJustiz, BNotK-Strukturdatensatz,
            NotarNow und DiNo-Exporten.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showAll}
              onChange={(_, checked) => setShowAll(checked)}
            />
          }
          label='Erledigte anzeigen'
        />
      </Box>

      <Stack direction='row' alignItems='center' gap={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size='small'
          placeholder='Suche nach Titel, Dialog oder Status …'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
        <ButtonGroup
          variant='contained'
          ref={splitButtonRef}
          sx={{ flexShrink: 0 }}
        >
          <Button startIcon={<Add />} onClick={() => openImport()}>
            Importieren
          </Button>
          <Button
            size='small'
            onClick={() => setIsFormatMenuOpen(o => !o)}
            aria-label='Importquelle wählen'
            sx={{ px: 0.5 }}
          >
            <ArrowDropDown />
          </Button>
        </ButtonGroup>
        <Popper
          open={isFormatMenuOpen}
          anchorEl={splitButtonRef.current}
          placement='bottom-end'
          transition
          disablePortal
          sx={{ zIndex: 10 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper elevation={3}>
                <ClickAwayListener
                  onClickAway={() => setIsFormatMenuOpen(false)}
                >
                  <MenuList autoFocusItem>
                    <MenuItem
                      onClick={triggerOpenFormularePull}
                      disabled={!ofVm.isReady || isPullingOpenFormulare}
                      sx={{ py: 1 }}
                    >
                      <CloudSync
                        fontSize='small'
                        color={ofVm.isReady ? 'success' : 'disabled'}
                        sx={{ mr: 1.5 }}
                      />
                      <ListItemText
                        primary={
                          isPullingOpenFormulare
                            ? 'OpenFormulare wird abgerufen …'
                            : `OpenFormulare (${ofVm.statusLabel})`
                        }
                        secondary={
                          ofVm.isReady
                            ? 'Dialog-Editor für Mandantenformulare — jetzt neue Einträge abrufen.'
                            : ofVm.hint
                        }
                        secondaryTypographyProps={{
                          sx: { whiteSpace: 'normal', maxWidth: 360 },
                        }}
                      />
                    </MenuItem>
                    {FORMAT_OPTIONS.map(o => (
                      <MenuItem
                        key={o.key}
                        onClick={() => openImport(o.key)}
                        sx={{ py: 1 }}
                      >
                        <ListItemText
                          primary={o.label}
                          secondary={o.hint}
                          secondaryTypographyProps={{
                            sx: {
                              whiteSpace: 'normal',
                              maxWidth: 360,
                            },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Stack>

      {isLoadingDialogInboxList ? (
        <CircularProgress />
      ) : isListEmpty ? (
        <Box>
          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Noch keine Einträge in der Dialoginbox.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Importieren Sie eine Datei aus einer der folgenden Quellen, um zu
            starten:
          </Typography>
          <Stack
            direction='row'
            gap={2}
            sx={{ flexWrap: 'wrap', alignItems: 'stretch' }}
          >
            <OpenFormulareCard
              onPullComplete={() => refetchDialogInboxList()}
            />
            {FORMAT_OPTIONS.map(o => (
              <Card
                key={o.key}
                variant='outlined'
                sx={{
                  flex: '1 1 220px',
                  minWidth: 220,
                  maxWidth: 320,
                  display: 'flex',
                }}
              >
                <CardActionArea
                  onClick={() => openImport(o.key)}
                  sx={{ height: '100%', alignItems: 'stretch' }}
                >
                  <CardContent>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      sx={{ mb: 1 }}
                    >
                      <Typography variant='subtitle1'>{o.label}</Typography>
                      <ArrowForward fontSize='small' color='action' />
                    </Stack>
                    <Typography variant='body2' color='text.secondary'>
                      {o.hint}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </Box>
      ) : (
        <DataGrid<IDialogInboxRow>
          autoHeight
          rows={filteredRows}
          columns={columns}
          getRowId={row => row.idDialogInbox}
          onRowClick={params => setSelectedId(params.row.idDialogInbox)}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'SubmittedAt', sort: 'desc' }] },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}

      {selectedId !== null && (
        <DialogInboxDetail
          idDialogInbox={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {isImportOpen && (
        <DialogInboxImportDialog
          initialFormat={initialFormat}
          onClose={() => setIsImportOpen(false)}
          onImported={entry => {
            setIsImportOpen(false);
            setSelectedId(entry.idDialogInbox);
          }}
        />
      )}
    </Box>
  );
};
